import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, Loader2, Clock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import { post, get } from '@/lib/api'

// ==================== Types ====================

interface UrgeResult {
  success: boolean
  message: string
  lastUrgeAt?: string
}

interface UrgeStatus {
  lastUrgeAt: string | null
  canUrge: boolean
}

type ApprovalType = 'leave' | 'reimbursement'

interface UrgeButtonProps {
  approvalType: ApprovalType
  approvalId: string
  className?: string
}

// ==================== API Functions ====================

async function urgeApproval(type: ApprovalType, id: string): Promise<UrgeResult> {
  const endpoint = type === 'leave' 
    ? `/hr/leaves/${id}/urge`
    : `/finance/reimbursements/${id}/urge`
  return post<UrgeResult>(endpoint)
}

async function getUrgeStatus(type: ApprovalType, id: string): Promise<UrgeStatus> {
  const endpoint = type === 'leave'
    ? `/hr/leaves/${id}/urge-status`
    : `/finance/reimbursements/${id}/urge-status`
  return get<UrgeStatus>(endpoint)
}

// ==================== Hook ====================

export function useUrge(type: ApprovalType, id: string) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const queryKey = ['urge-status', type, id]

  const { data: status, isLoading: isLoadingStatus } = useQuery({
    queryKey,
    queryFn: () => getUrgeStatus(type, id),
  })

  const urgeMutation = useMutation({
    mutationFn: () => urgeApproval(type, id),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: (error: Error) => {
      toast.error(error.message || '催办失败')
    },
  })

  return {
    status,
    isLoadingStatus,
    urge: urgeMutation.mutate,
    isUrging: urgeMutation.isPending,
  }
}

// ==================== Component ====================

export function UrgeButton({ approvalType, approvalId, className }: UrgeButtonProps) {
  const { status, isLoadingStatus, urge, isUrging } = useUrge(approvalType, approvalId)

  const formatLastUrgeTime = (isoString: string) => {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffMinutes < 60) {
      return `${diffMinutes}分钟前`
    }
    if (diffHours < 24) {
      return `${diffHours}小时前`
    }
    return date.toLocaleDateString('zh-CN')
  }

  if (isLoadingStatus) {
    return (
      <Button variant="outline" size="sm" disabled className={className}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    )
  }

  const canUrge = status?.canUrge ?? true
  const lastUrgeAt = status?.lastUrgeAt

  if (!canUrge && lastUrgeAt) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        disabled 
        className={className}
        title={`上次催办: ${formatLastUrgeTime(lastUrgeAt)} (24小时内只能催办一次)`}
      >
        <Clock className="mr-1.5 h-4 w-4" />
        已催办
      </Button>
    )
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => urge()}
      disabled={isUrging}
      className={className}
    >
      {isUrging ? (
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
      ) : (
        <Bell className="mr-1.5 h-4 w-4" />
      )}
      催办
    </Button>
  )
}
